/*
  "Live" Predictive Search: vanilla JS module.
  Handles drawer open/close, focus trapping, body scroll lock,
  and fetching predictive search results using Shopify's Suggest API.
*/

(function () {
  'use strict';

  var FOCUSABLE = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  var searchDrawerElement = null;
  var panel = null;
  var input = null;
  var resultsContainer = null;
  var lastOpener = null;
  var isOpen = false;
  var debounceTimeout = null;
  var currentSearchQuery = '';

  // Simple money formatter for Shopify cents to "X,XX PLN" (matching cart-drawer.js style)
  function formatMoney(cents) {
    var amount = (cents / 100).toFixed(2);
    amount = amount.replace('.', ',');
    return amount + ' PLN';
  }

  function init() {
    searchDrawerElement = document.querySelector('[data-search-drawer]');
    if (!searchDrawerElement) return;

    panel = searchDrawerElement.querySelector('[data-search-drawer-panel]');
    input = searchDrawerElement.querySelector('[data-search-drawer-input]');
    resultsContainer = searchDrawerElement.querySelector('[data-search-drawer-results]');

    if (!panel || !input || !resultsContainer) return;

    setupEvents();
  }

  function setupEvents() {
    // Open trigger (delegated)
    document.addEventListener('click', function (event) {
      var opener = event.target.closest('[data-search-drawer-open]');
      if (opener) {
        event.preventDefault();
        open(opener);
      }
    });

    // Close buttons/backdrop
    searchDrawerElement.querySelectorAll('[data-search-drawer-close]').forEach(function (el) {
      el.addEventListener('click', function (event) {
        event.preventDefault();
        close();
      });
    });

    // Handle typing in input with debounce
    input.addEventListener('input', function () {
      var query = input.value.trim();
      if (query === currentSearchQuery) return;
      currentSearchQuery = query;

      clearTimeout(debounceTimeout);

      if (query.length < 2) {
        renderPrompt();
        return;
      }

      renderLoading();
      debounceTimeout = setTimeout(function () {
        fetchResults(query);
      }, 300);
    });

    // Submit form check
    var form = searchDrawerElement.querySelector('form');
    if (form) {
      form.addEventListener('submit', function (event) {
        if (!input.value.trim()) {
          event.preventDefault();
        }
      });
    }
  }

  function getFocusable() {
    var nodes = panel.querySelectorAll(FOCUSABLE);
    return Array.prototype.filter.call(nodes, function (el) {
      return !el.hasAttribute('disabled') && el.offsetParent !== null;
    });
  }

  function open(opener) {
    if (isOpen) return;
    isOpen = true;
    lastOpener = opener || null;

    searchDrawerElement.hidden = false;
    document.documentElement.classList.add('is-search-drawer-open');
    document.body.classList.add('is-search-drawer-open');

    // next frame so the transition can run
    requestAnimationFrame(function () {
      searchDrawerElement.classList.add('is-open');
      searchDrawerElement.setAttribute('aria-hidden', 'false');
      input.setAttribute('aria-expanded', 'true');
    });

    // Focus on input
    setTimeout(function () {
      input.focus();
    }, 100);

    document.addEventListener('keydown', onKeydown, true);
  }

  function close() {
    if (!isOpen) return;
    isOpen = false;

    searchDrawerElement.classList.remove('is-open');
    searchDrawerElement.setAttribute('aria-hidden', 'true');
    input.setAttribute('aria-expanded', 'false');

    document.documentElement.classList.remove('is-search-drawer-open');
    document.body.classList.remove('is-search-drawer-open');

    document.removeEventListener('keydown', onKeydown, true);

    // Wait for animation before hiding drawer entirely
    var styles = window.getComputedStyle(panel);
    var duration = parseDuration(styles.transitionDuration);
    var hide = function () {
      if (!isOpen) {
        searchDrawerElement.hidden = true;
        // Reset query and display
        input.value = '';
        currentSearchQuery = '';
        renderPrompt();
      }
    };

    if (duration > 0) {
      setTimeout(hide, duration);
    } else {
      hide();
    }

    if (lastOpener && typeof lastOpener.focus === 'function') {
      lastOpener.focus({ preventScroll: true });
    }
    lastOpener = null;
  }

  function onKeydown(event) {
    if (event.key === 'Escape' || event.key === 'Esc') {
      event.preventDefault();
      close();
      return;
    }

    if (event.key === 'Tab') {
      var focusable = getFocusable();
      if (!focusable.length) {
        event.preventDefault();
        panel.focus({ preventScroll: true });
        return;
      }
      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      var active = document.activeElement;

      if (event.shiftKey) {
        if (active === first || active === panel || !panel.contains(active)) {
          event.preventDefault();
          last.focus({ preventScroll: true });
        }
      } else if (active === last) {
        event.preventDefault();
        first.focus({ preventScroll: true });
      }
    }
  }

  // Event-day menu items are tagged `menu-only`: they belong to the kitchen and
  // bar pages, which are reachable by scanning a QR code at the venue and by no
  // other route. Search must not be the back door, so they are dropped here. We
  // ask for more than we show because the filter runs after the fetch.
  var MENU_ONLY_TAG = 'menu-only';
  var RESULT_LIMIT = 6;

  function isMenuOnly(product) {
    var tags = (product && product.tags) || [];
    for (var i = 0; i < tags.length; i++) {
      if (String(tags[i]).toLowerCase() === MENU_ONLY_TAG) return true;
    }
    return false;
  }

  function fetchResults(query) {
    var url = '/search/suggest.json?q=' + encodeURIComponent(query)
      + '&resources[type]=product&resources[limit]=10';

    fetch(url)
      .then(function (response) {
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        return response.json();
      })
      .then(function (suggestions) {
        if (currentSearchQuery !== query) return; // ignore stale responses
        renderResults(suggestions, query);
      })
      .catch(function (error) {
        console.error('Predictive search error:', error);
        if (currentSearchQuery === query) {
          renderError();
        }
      });
  }

  function renderPrompt() {
    var promptText = searchDrawerElement.getAttribute('data-t-prompt') || 'Start typing to search...';
    resultsContainer.innerHTML = '<div class="search-drawer__prompt">' + escapeHtml(promptText) + '</div>';
  }

  function renderLoading() {
    var loadingText = searchDrawerElement.getAttribute('data-t-loading') || 'Loading...';
    resultsContainer.innerHTML = '<div class="search-drawer__loading"><div class="search-drawer__spinner"></div>' + escapeHtml(loadingText) + '</div>';
  }

  function renderError() {
    var errorText = searchDrawerElement.getAttribute('data-t-error')
      || 'Помилка пошуку. Будь ласка, спробуйте ще раз.';
    resultsContainer.innerHTML = '<div class="search-drawer__error">' + escapeHtml(errorText) + '</div>';
  }

  function renderResults(suggestions, query) {
    var products = [];
    if (suggestions && suggestions.resources && suggestions.resources.results) {
      products = suggestions.resources.results.products || [];
    }
    products = products.filter(function (p) { return !isMenuOnly(p); }).slice(0, RESULT_LIMIT);

    if (!products.length) {
      var noResultsTemplate = searchDrawerElement.getAttribute('data-t-no-results') || 'No results found for "[QUERY]"';
      var noResultsText = noResultsTemplate.replace('[QUERY]', query);
      resultsContainer.innerHTML = '<div class="search-drawer__no-results">' + escapeHtml(noResultsText) + '</div>';
      return;
    }

    var headingText = searchDrawerElement.getAttribute('data-t-products') || 'Products';
    var allResultsTemplate = searchDrawerElement.getAttribute('data-t-all-results') || 'Show all results for "[QUERY]"';
    var allResultsText = allResultsTemplate.replace('[QUERY]', query);

    var html = '';
    html += '<h3 class="search-drawer__results-heading">' + escapeHtml(headingText) + '</h3>';
    html += '<ul class="search-drawer__products-list" role="listbox" aria-label="' + escapeHtml(headingText) + '">';

    products.forEach(function (product, index) {
      var priceStr = '';
      if (product.price) {
        // Handle string cents or numeric price
        var rawPrice = parseFloat(product.price);
        if (!isNaN(rawPrice)) {
          priceStr = formatMoney(rawPrice);
        } else {
          priceStr = product.price;
        }
      }

      var imageHtml = '';
      if (product.image) {
        imageHtml = '<img class="search-drawer__item-image" src="' + product.image + '" alt="' + escapeHtml(product.title) + '" width="50" height="50" loading="lazy">';
      } else {
        imageHtml = '<div class="search-drawer__item-image search-drawer__item-image--placeholder"></div>';
      }

      html += '<li class="search-drawer__products-item" role="option" id="predictive-search-option-' + index + '">';
      html += '  <a href="' + product.url + '" class="search-drawer__item-link">';
      html += '    ' + imageHtml;
      html += '    <div class="search-drawer__item-info">';
      html += '      <span class="search-drawer__item-title">' + escapeHtml(product.title) + '</span>';
      html += '      <span class="search-drawer__item-price">' + escapeHtml(priceStr) + '</span>';
      html += '    </div>';
      html += '  </a>';
      html += '</li>';
    });

    html += '</ul>';

    // Show All Results Link
    html += '<div class="search-drawer__footer">';
    html += '  <a href="/search?q=' + encodeURIComponent(query) + '" class="button button--secondary search-drawer__all-link">';
    html += '    <span class="button__label">' + escapeHtml(allResultsText) + '</span>';
    html += '  </a>';
    html += '</div>';

    resultsContainer.innerHTML = html;
  }

  function escapeHtml(str) {
    if (!str) return '';
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function parseDuration(value) {
    if (!value) return 0;
    var parts = value.split(',');
    var max = 0;
    parts.forEach(function (raw) {
      var v = raw.trim();
      var ms = v.endsWith('ms')
        ? parseFloat(v)
        : parseFloat(v) * 1000;
      if (!isNaN(ms) && ms > max) max = ms;
    });
    return max;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
